import type {
  TabListProps as AriaTabListProps,
  TabPanelProps as AriaTabPanelProps,
  TabProps as AriaTabProps,
  TabsProps as AriaTabsProps,
} from 'react-aria-components';
import {
  Tab as AriaTab,
  TabList as AriaTabList,
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
} from 'react-aria-components';

import { cx } from '@/utils/cx';

// ─── Tabs (root) ─────────────────────────────────────────────────────────────

type TabsProps = AriaTabsProps;

const Tabs = (props: TabsProps) => {
  return (
    <AriaTabs
      {...props}
      className={state =>
        cx(
          'flex flex-col',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className
        )
      }
    />
  );
};

// ─── TabList ─────────────────────────────────────────────────────────────────

type TabListProps<T extends object> = AriaTabListProps<T>;

const TabList = <T extends object>(props: TabListProps<T>) => {
  return (
    <AriaTabList
      {...props}
      className={state =>
        cx(
          'flex gap-1 border-b border-[var(--color-border-secondary)]',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className
        )
      }
    />
  );
};

// ─── Tab ─────────────────────────────────────────────────────────────────────

type TabProps = AriaTabProps;

const Tab = (props: TabProps) => {
  return (
    <AriaTab
      {...props}
      className={state =>
        cx(
          '-mb-px cursor-pointer border-b-2 px-3 py-2 text-sm font-medium outline-hidden transition-colors',
          state.isSelected
            ? 'border-[var(--color-border-brand)] text-[var(--color-text-primary)]'
            : 'border-transparent text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
          state.isDisabled && 'cursor-not-allowed opacity-50',
          state.isFocusVisible &&
            'outline-2 outline-offset-2 outline-focus-ring',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className
        )
      }
    />
  );
};

// ─── TabPanel ────────────────────────────────────────────────────────────────

type TabPanelProps = AriaTabPanelProps;

const TabPanel = (props: TabPanelProps) => {
  return (
    <AriaTabPanel
      {...props}
      className={state =>
        cx(
          'pt-4 outline-hidden',
          state.isFocusVisible &&
            'outline-2 outline-offset-2 outline-focus-ring',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className
        )
      }
    />
  );
};

export { Tab, TabList, TabPanel, Tabs };
